import { Box, MenuItem, Stack, IconButton } from '@mui/material';
import { common } from '@mui/material/colors';
import BaseNumberField from 'common/components/base-number-field';
import { BaseSelectField } from 'common/components/base-select-field';
import BaseTextField from 'common/components/base-text-field';
import RequiredLabel from 'common/components/required-label';
import { styleInput, styleInputNumber } from 'common/constants';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { createStyleSheet, handleError } from 'utils/helpers';
import {
    IQuestionAnswer,
    IQuestionBaseProps,
    IQuestionNewEditForm,
} from 'types/campaign-design';
import QuestionDnD from './question-dnd';
import MinusIcon from '@mui/icons-material/Remove';
import PlusIcon from '@mui/icons-material/Add';
import ModalConfirm from 'common/components/modal-confirm';
import { deleteAnswerOptions } from 'services/campaign.service';
import { useParams } from 'react-router-dom';
// import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import { removeSkipLogicFromQuestionAffected } from 'slices/campaignsSlice';

const QuestionDropdown: React.FC<IQuestionBaseProps> = ({
    question,
    ...props
}) => {
    const { watch, control, setValue } = useFormContext();
    const { insert, remove } = useFieldArray({
        control,
        name: `data.answers`,
    });

    const numberOfRows = watch(`data.numberOfRows`);
    const answers = watch('data.answers');
    const [openModalDeleteAnswer, setOpenModalDeleteAnswer] =
        React.useState(false);
    const [modalDeleteAnswerText, setModalDeleteAnswerText] =
        React.useState<string>('');
    const [modalDeleteAnswerIndex, setModalDeleteAnswerIndex] =
        React.useState<number>(0);

    const questions = useAppSelector(
        (state: RootState) => state.campaignState.campaignQuestions
    );
    const [rootListQuestionsAffected, setRootListQuestionsAffected] =
        React.useState<
            {
                questionNumber: number | null | undefined;
                question: string;
                questionId?: number | null | string;
                skipId?: number | null | string;
            }[]
        >([]);
    const checkRemoveAnswer = (fieldIndex: number) => {
        const listQuestionsAffected: {
            questionNumber: number | null | undefined;
            question: string;
            questionId?: number | null | string;
            skipId?: number | null | string;
        }[] = [];
        if (question.creeningLogic && question.creeningLogic.length > 0) {
            const findAnswer = question.creeningLogic.find(
                (item) => item.answer_id === answers[fieldIndex].id
            );
            if (findAnswer) {
                listQuestionsAffected.push({
                    questionNumber: question.questionNumber,
                    question: question.question,
                });
            }
        }
        questions.forEach((item: IQuestionNewEditForm) => {
            if (item.skipLogic && item.skipLogic.length > 0) {
                const findLogic = item.skipLogic.find(
                    (item) => item.answer_id === answers[fieldIndex].id
                );
                if (findLogic) {
                    listQuestionsAffected.push({
                        questionNumber: item.questionNumber,
                        question: item.question,
                        questionId: item.id,
                        skipId: findLogic.id,
                    });
                }
            }
        });
        if (listQuestionsAffected.length > 0) {
            const messageQuestion = listQuestionsAffected
                .map((item) => `Q${item.questionNumber}. ${item.question}`)
                .join(', ');
            setModalDeleteAnswerIndex(fieldIndex);
            setModalDeleteAnswerText(
                `Xoá đáp án này sẽ ảnh hưởng đến logic ở câu hỏi: ${messageQuestion}. Bạn có chắc chắn muốn xoá không?`
            );
            setRootListQuestionsAffected(listQuestionsAffected);
            setOpenModalDeleteAnswer(true);
        } else {
            removeAnswer(fieldIndex);
        }
    };

    const campaignId = useParams<{ id: string }>().id;

    const dispatch = useAppDispatch();
    const removeAnswer = async (fieldIndex: number) => {
        remove(fieldIndex);
        setValue('data.numberOfRows', numberOfRows ? numberOfRows - 1 : 0);
        if (!question.id) {
            return;
        }
        try {
            const res = await deleteAnswerOptions(
                Number(campaignId),
                Number(question.id),
                answers[fieldIndex].id
            );
            if (res.success) {
                // props.replaceQuestionByQuestionId(
                //     Number(question.id),
                //     listCampaignQuestionsFormatter(res.data.question)
                // );
                dispatch(
                    removeSkipLogicFromQuestionAffected({
                        listAffected: rootListQuestionsAffected.map((item) => ({
                            questionId: item.questionId,
                            skipId: item.skipId,
                        })),
                    })
                );
                setRootListQuestionsAffected([]);
                toast.success(res.message);
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            handleError(error);
        }
    };

    const insertAnswer = (fieldIndex: number) => {
        insert(fieldIndex + 1, {
            id: null,
            answer: `Tuỳ chọn ${numberOfRows + 1}`,
        });
        setValue('data.numberOfRows', numberOfRows + 1);
    };

    return (
        <Box>
            <Stack p={'0px 35px 35px 35px'} gap={2}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                    <QuestionDnD {...props} question={question} />
                    {!question.answerFromOtherQuestionLogic?.id &&
                        answers?.length === 0 && (
                            <Box
                                display={'flex'}
                                alignItems={'center'}
                                width={'100%'}
                            >
                                <IconButton onClick={() => insertAnswer(0)}>
                                    <PlusIcon />
                                </IconButton>
                                <Box component={'span'}>Thêm đáp án</Box>
                            </Box>
                        )}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            width: '100%',
                        }}
                    >
                        {answers?.map(
                            (item: IQuestionAnswer, fieldIndex: number) => {
                                const answerValue = watch(
                                    `data.answers.${fieldIndex}.answer`
                                );
                                return (
                                    <Box
                                        key={`${item.id}_${fieldIndex}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            gap: 1,
                                        }}
                                    >
                                        <BaseTextField
                                            autoComplete="off"
                                            fullWidth
                                            name={`data.answers.${fieldIndex}.answer`}
                                            value={answerValue}
                                            size="small"
                                            placeholder={`Nhập nội dung hàng ${fieldIndex + 1}`}
                                            sx={styles.answer}
                                            InputProps={{
                                                inputProps: {
                                                    maxLength: 255,
                                                },
                                            }}
                                        />
                                        <Box
                                            display={'flex'}
                                            marginLeft={'15px'}
                                        >
                                            {answers.length > 1 && (
                                                <IconButton
                                                    onClick={() =>
                                                        checkRemoveAnswer(
                                                            fieldIndex
                                                        )
                                                    }
                                                >
                                                    <MinusIcon />
                                                </IconButton>
                                            )}

                                            <IconButton
                                                onClick={() =>
                                                    insertAnswer(fieldIndex)
                                                }
                                            >
                                                <PlusIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                );
                            }
                        )}
                    </Box>
                </Box>
            </Stack>
            <Stack
                flexDirection={'row'}
                flexWrap={'wrap'}
                gap={4}
                p={'35px'}
                sx={{
                    borderTop: `2px dashed var(--color-primary)`,
                }}
            >
                <Stack flexDirection={'column'} gap={'20px'} display={'none'}>
                    <RequiredLabel
                        label="Số hàng"
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseNumberField
                        disabled
                        sx={styleInputNumber}
                        size="small"
                        name={`data.numberOfRows`}
                        min={1}
                        max={20}
                        InputProps={{
                            readOnly: true,
                        }}
                        forceDisableMinus={
                            !!question.answerFromOtherQuestionLogic?.id
                        }
                        forceDisablePlus={
                            !!question.answerFromOtherQuestionLogic?.id
                        }
                        showMinus={false}
                        showPlus={false}
                    />
                </Stack>
                <Stack flexDirection={'column'} gap={'20px'}>
                    <RequiredLabel
                        label={`Sắp xếp các đáp án`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseSelectField
                        name={`data.sortAnswers`}
                        size="small"
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '24px',
                                backgroundColor: common.white,
                            },
                        }}
                    >
                        <MenuItem value={'default'}>Mặc định</MenuItem>
                        <MenuItem value={'AZ'}>A-Z</MenuItem>
                        <MenuItem value={'ZA'}>Z-A</MenuItem>
                        <MenuItem value={'random'}>Ngẫu nhiên</MenuItem>
                    </BaseSelectField>
                </Stack>
            </Stack>
            {openModalDeleteAnswer && (
                <ModalConfirm
                    open={openModalDeleteAnswer}
                    onClose={() => setOpenModalDeleteAnswer(false)}
                    title={'Xác nhận xóa đáp án'}
                    textOk="Xóa"
                    onOk={() => {
                        removeAnswer(modalDeleteAnswerIndex);
                        setOpenModalDeleteAnswer(false);
                        setModalDeleteAnswerText('');
                        setModalDeleteAnswerIndex(0);
                    }}
                    content={modalDeleteAnswerText}
                />
            )}
        </Box>
    );
};

const styles = createStyleSheet({
    answer: {
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            height: '65px',
            borderRadius: '50px',
        },
    },
});
export default QuestionDropdown;
